import React, { useState } from 'react'
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import RadialBackgroundContainer from './RadialBackgroundContainer'
import Button from './Button'
import Checkbox from './Checkbox'
import StatusPill from './StatusPill'
import { ChevronDownIcon, ClockIcon, CalendarIcon, UsersIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { LocalTimeBadge } from '../shared/components/LocalTimeBadge'
import IconWithBackground from './IconWithBackground'
import { INTERAX_COLORS } from '../constants/interaxColors'
import { SessionDateBlock } from './SessionDateBlock'
import { TimeDisplay } from './TimeDisplay'
import { LiveStatusPill } from './LiveStatusPill'

// Session Item Types
export type SessionItemType = 'weekly' | 'upcoming'

// Button Types for SessionItem
export type SessionButtonType = 'join' | 'view-details' | 'send-invites' | 'view-feedback' | 'custom'

// Button Configuration
export interface SessionButton {
  type: SessionButtonType
  label: string
  onClick: () => void
}

// Weekly Session Data Structure
export interface WeeklySessionData {
  day: number
  month: string
  title: string
  teams: number
  participants: number
  status: 'live' | 'upcoming'
  time?: string // Only for upcoming sessions
}

// Upcoming Session Data Structure
export interface UpcomingSessionData {
  day: number
  month: string
  title: string
  sprintName?: string // Shown in header for quick visibility
  teams: number
  participants: number
  time?: string // Start time (e.g., "14:00" or "2:00 PM")
  status?: 'live' | 'upcoming' | 'completed' // For displaying status pill
  buttonText: string
  onButtonClick: () => void
  buttonIcon?: React.ReactNode // Optional icon for the button
  // Accordion support
  expandable?: boolean
  details?: React.ReactNode | string
}

// Legacy support for existing SessionItem usage
export type SessionRight =
  | { type: 'none' }
  | { type: 'pill'; pill: { status: 'active' | 'upcoming' | 'draft' | 'completed' | 'live'; label?: string } }
  | { type: 'button'; label: string; onClick: () => void }
  | { type: 'button-x'; label: string; onClick: () => void; onClose: () => void }

export interface SessionMeta {
  time?: string
  date?: string
  team?: string
  loops?: number
}

export interface SessionItemProps {
  // New type-based props
  type?: SessionItemType
  weeklyData?: WeeklySessionData
  upcomingData?: UpcomingSessionData

  // Legacy props for backward compatibility
  icon?: React.ReactNode
  title?: string
  meta?: SessionMeta
  right?: SessionRight
  selectable?: boolean
  selected?: boolean
  onSelectedChange?: (selected: boolean) => void
  className?: string
}

// Weekly Session Component
const WeeklySessionItem: React.FC<{ data: WeeklySessionData; className?: string }> = ({ data, className = '' }) => {
  const { t } = useTranslation()
  return (
    <RadialBackgroundContainer className={`flex items-center gap-4 ${className}`}>
      {/* Left: Date Block */}
      <div className="flex flex-col items-center min-w-[60px]">
        <div className="text-3xl font-bold" style={{ color: INTERAX_COLORS.sun }}>
          {data.day}
        </div>
        <div className="text-sm font-medium" style={{ color: INTERAX_COLORS.thunderGrey }}>
          {data.month}
        </div>
      </div>

      {/* Center: Session Details */}
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-lg truncate">{data.title}</div>
        <div className="text-sm mt-1" style={{ color: INTERAX_COLORS.thunderGrey }}>
          {data.teams} {t('common.teams')} • {data.participants} {t('common.participants')}
        </div>
      </div>

      {/* Right: Status/Time */}
      <div className="flex items-center">
        {data.status === 'live' ? (
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-green-400 text-sm font-medium">{t('common.live')}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/20">
            <span className="text-sm font-medium" style={{ color: INTERAX_COLORS.sun }}>
              {data.time}
            </span>
          </div>
        )}
      </div>
    </RadialBackgroundContainer>
  )
}

// Upcoming Session Component (with optional accordion)
const UpcomingSessionItem: React.FC<{ data: UpcomingSessionData; className?: string }> = ({ data, className = '' }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = () => {
    if (data.expandable) {
      setIsExpanded(!isExpanded)
    }
  }

  return (
    <div className={className}>
      {/* Session Summary (always visible) */}
      <RadialBackgroundContainer
        useFixedHeight={!isMobile}
        className={`flex ${data.expandable ? 'cursor-pointer' : ''} ${
          isMobile ? 'flex-col items-stretch gap-3 py-3' : 'flex-row items-center gap-4'
        }`}
        onClick={data.expandable ? toggleExpanded : undefined}
      >
        {/* Row 1 on mobile: Date + Content | Row 1 on desktop: Date + Content + Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 2,
            flex: 1,
            minWidth: 0
          }}
        >
          {/* Date Block */}
          <SessionDateBlock day={data.day} month={data.month} />

          {/* Center: Session Details with Time */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden'
            }}
          >
            <Tooltip
              title={data.sprintName ? `${data.title} • ${data.sprintName}` : data.title}
              placement="top"
              enterDelay={300}
            >
              <Box
                component="span"
                sx={{
                  display: isMobile ? '-webkit-box' : 'block',
                  color: INTERAX_COLORS.white,
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: isMobile ? 'normal' : 'nowrap',
                  ...(isMobile && {
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as const
                  })
                }}
              >
                {data.title}
                {data.sprintName && (
                  <>
                    {' • '}
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {data.sprintName}
                    </Box>
                  </>
                )}
              </Box>
            </Tooltip>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                mt: 0.25
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: '0.875rem',
                  color: INTERAX_COLORS.thunderGrey,
                  whiteSpace: isMobile ? 'normal' : 'nowrap'
                }}
              >
                {data.teams} {t('common.teams')} • {data.participants} {t('common.participants')}
              </Box>

              {/* Time Display - Close to participants text */}
              {data.time && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <TimeDisplay time={data.time} status={data.status || 'upcoming'} />
                  <LocalTimeBadge />
                </Box>
              )}

              {/* Status Pill (for live or completed sessions without time) */}
              {!data.time && data.status === 'live' && <LiveStatusPill />}
              {data.status === 'completed' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.25,
                    borderRadius: '9999px',
                    backgroundColor: `${INTERAX_COLORS.green}20`,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: INTERAX_COLORS.green
                  }}
                >
                  Completed
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: Button + Expand - full width on mobile, auto on desktop */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-end' : 'flex-start',
            gap: 1,
            flexShrink: 0
          }}
        >
          {/* Action Button - Only show if buttonText is not empty */}
          {data.buttonText && (
            <Button
              variant="primary"
              onClick={(e?: React.MouseEvent) => {
                if (e) {
                  e.stopPropagation() // Prevent accordion toggle
                }
                data.onButtonClick()
              }}
              className="px-4 py-2 flex items-center gap-2"
            >
              {data.buttonIcon && <span className="inline-flex">{data.buttonIcon}</span>}
              {data.buttonText}
            </Button>
          )}

          {/* Expand Icon (if expandable) */}
          {data.expandable && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 200ms',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            >
              <ChevronDownIcon className="w-5 h-5" style={{ color: INTERAX_COLORS.white }} />
            </Box>
          )}
        </Box>
      </RadialBackgroundContainer>

      {/* Accordion Details (only shown when expanded) */}
      {data.expandable && isExpanded && data.details && (
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: '8px',
            backgroundColor: `${INTERAX_COLORS.heritageBlue}40`,
            border: `1px solid ${INTERAX_COLORS.white20}`
          }}
        >
          {typeof data.details === 'string' ? (
            <Box
              sx={{
                fontSize: '0.875rem',
                color: INTERAX_COLORS.white
              }}
            >
              {data.details}
            </Box>
          ) : (
            data.details
          )}
        </Box>
      )}
    </div>
  )
}

// Legacy MetaRow for backward compatibility
const MetaRow: React.FC<{ meta?: SessionMeta }> = ({ meta }) => {
  const { t } = useTranslation()
  if (!meta) return null
  const parts: React.ReactNode[] = []
  if (meta.time)
    parts.push(
      <span key="time" className="flex items-center gap-2 text-thunder-grey">
        <ClockIcon className="w-4 h-4" />
        {meta.time}
      </span>
    )
  if (meta.date)
    parts.push(
      <span key="date" className="flex items-center gap-2 text-thunder-grey">
        <CalendarIcon className="w-4 h-4" />
        {meta.date}
      </span>
    )
  if (meta.team)
    parts.push(
      <span key="team" className="flex items-center gap-2 text-thunder-grey">
        <UsersIcon className="w-4 h-4" />
        {meta.team}
      </span>
    )
  if (typeof meta.loops === 'number')
    parts.push(
      <span key="loops" className="flex items-center gap-2 text-thunder-grey">
        <ArrowPathIcon className="w-4 h-4" />
        {meta.loops} {t('common.loops')}
      </span>
    )

  return (
    <div className="flex items-center gap-4 text-body-3">
      {parts.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          {p}
        </div>
      ))}
    </div>
  )
}

// Legacy SessionItem for backward compatibility
const LegacySessionItem: React.FC<SessionItemProps> = ({
  icon,
  title = '',
  meta,
  right = { type: 'none' },
  selectable = false,
  selected = false,
  onSelectedChange,
  className = ''
}) => {
  const { t } = useTranslation()
  return (
    <RadialBackgroundContainer className={`flex ${selected ? 'ring-2 ring-sun border-sun' : ''} ${className}`}>
      {/* Left: icon */}
      <IconWithBackground icon={icon ?? <ClockIcon className="w-6 h-6" />} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-headline-5 text-white truncate">{t(title)}</div>
        <MetaRow meta={meta} />
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-3">
        {right.type === 'pill' && (
          <StatusPill status={right.pill.status} label={right.pill.label ? t(right.pill.label) : undefined} />
        )}
        {right.type === 'button' && (
          <Button variant="primary" onClick={right.onClick}>
            {' '}
            {t(right.label)}{' '}
          </Button>
        )}
        {right.type === 'button-x' && (
          <>
            <Button variant="primary" onClick={right.onClick}>
              {' '}
              {t(right.label)}{' '}
            </Button>
            <button
              aria-label="close"
              className="w-8 h-8 rounded-lg border-2 border-sun-40 text-white/90 hover:text-white"
            >
              ✕
            </button>
          </>
        )}
        {selectable && <Checkbox checked={selected} onChange={onSelectedChange ?? (() => {})} />}
      </div>
    </RadialBackgroundContainer>
  )
}

// Main SessionItem Component
const SessionItem: React.FC<SessionItemProps> = (props) => {
  const { type, weeklyData, upcomingData, className = '' } = props

  // New type-based rendering
  if (type === 'weekly' && weeklyData) {
    return <WeeklySessionItem data={weeklyData} className={className} />
  }

  if (type === 'upcoming' && upcomingData) {
    return <UpcomingSessionItem data={upcomingData} className={className} />
  }

  // Legacy rendering for backward compatibility
  return <LegacySessionItem {...props} />
}

export default SessionItem
